import { useQuery } from '@tanstack/react-query';
import type { ExtensionTargetModel, ExtensionValue } from '@erp/shared-types';
import useAuth from '@/api/auth/useAuth';
import { rpcClient } from '@/api/rpc/client';

/** Shares the invalidation prefix used by registration/sync and exposure settings. */
export function useExtensionColumnSources(tableId: string, enabled = true) {
  const { currentUser } = useAuth();
  return useQuery({
    queryKey: [
      'schema-extensions',
      'column-sources',
      currentUser?.id,
      currentUser?.organizationId,
      tableId,
    ],
    queryFn: () => rpcClient.core.schemaExtensions.columnSources({ tableId }),
    enabled: enabled && !!currentUser,
    staleTime: 0,
    retry: false,
  });
}

export function useExtensionDetail(
  targetModel: ExtensionTargetModel,
  entityKey: string,
  extensionId: string,
  enabled: boolean,
) {
  const { currentUser } = useAuth();
  const input = {
    targetModel,
    entityKey,
    extensionIds: [extensionId.toLowerCase()],
    surface: 'detail' as const,
  };
  return useQuery({
    queryKey: [
      'schema-extensions',
      'entity-view',
      currentUser?.id,
      currentUser?.organizationId,
      input,
    ],
    queryFn: () => rpcClient.core.schemaExtensions.entityViews(input),
    enabled: enabled && !!currentUser,
    // Revalidate every activation. Never display cached values while refetching.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/**
 * Every detail-exposed extension field of one entity, across all its
 * extensions: the field metadata and the entity's values keyed by `fieldKey`.
 * Disabled while the entity is unknown.
 */
export function useExtensionEntityValues(
  targetModel: ExtensionTargetModel,
  entityKey: string | null,
) {
  const { currentUser } = useAuth();
  const input = {
    targetModel,
    entityKey: entityKey ?? '',
    surface: 'detail' as const,
  };
  return useQuery({
    queryKey: [
      'schema-extensions',
      'entity-view',
      currentUser?.id,
      currentUser?.organizationId,
      input,
    ],
    queryFn: () => rpcClient.core.schemaExtensions.entityViews(input),
    enabled: !!entityKey && !!currentUser,
    select: (response) => ({
      fields: response.extensionColumns,
      values: Object.assign(
        {},
        ...response.views.map((view) => view.state.values),
      ) as Record<string, ExtensionValue>,
    }),
    retry: false,
  });
}
